import {
  calculateDiscount,
  calculateGeneralValues,
  calculatePrice,
} from "@/helper/property";

import {
  banks,
  todayUnixUTC,
  modalityCriteria,
  typeCriteria,
} from "@/constants";

import { upperFirst } from "@/helper/string";

import type { House, LeilaoSQLCustom, Modality } from "@/types";

type EstateWithBank = Pick<House, "numero_do_leilao" | "banco">;

export const removeAccents = (value: string) => {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const formatStringArraySearchParam = (values?: string | string[]) =>
  Array.isArray(values)
    ? values.join(", ")
    : typeof values === "string"
      ? (values as unknown as string).split(",").join(", ")
      : "";

export const formatPropertyDescriptionJsonLd = (
  property: Pick<
    LeilaoSQLCustom,
    | "uf"
    | "tipo"
    | "cidade_descricao"
    | "bairro"
    | "endereco"
    | "total_area_privativa"
    | "total_area"
    | "total_area_terreno"
    | "banco"
  >
) => {
  const area =
    property.total_area_privativa ||
    property.total_area ||
    property.total_area_terreno;

  return `${formatPropertyTitle(property)}, ${
    property.endereco
  }, ${property.uf!.toUpperCase()}. Área: ${area}m2`;
};

export const formatPropertyTitle = (
  property: Pick<
    LeilaoSQLCustom,
    "tipo" | "cidade_descricao" | "bairro" | "banco"
  >,
  options?: {
    showBank?: boolean;
    showAuction?: boolean;
  }
) => {
  const type = upperFirst(property.tipo!);
  const location = `${property.cidade_descricao}, ${property.bairro}`;

  if (options?.showBank) {
    if (options?.showAuction) {
      return `${type} ${upperFirst(property.banco)} em Leilão em ${location}`;
    }

    return `${type} ${upperFirst(property.banco)} em ${location}`;
  }

  if (options?.showAuction) {
    return `${type} em Leilão em ${location}`;
  }

  return `${type} em ${location}`;
};

const formatStringUrl = (value: string | null) =>
  value
    ? value
        .toLowerCase()
        .replace(/\br\./g, "rua")
        .replace(/-r-/g, "-rua-")
        .replace(/[\s\-,\/.]/g, "-")
        .replace(/[^a-záàâãéèêíïóôõöúçñ0-9-]/g, "")
        .replace(/-+/g, "-")
    : "";

export const formatPropertyTitleUrl = (
  property: Pick<
    LeilaoSQLCustom,
    | "uf"
    | "tipo"
    | "cidade_descricao"
    | "bairro"
    | "total_area_privativa"
    | "total_area"
    | "total_area_terreno"
    | "banco"
    | "endereco"
  >
) => {
  const cidade = formatStringUrl(property.cidade_descricao);
  const bairro = formatStringUrl(property.bairro);
  const endereco = formatStringUrl(property.endereco);
  const tipo = formatStringUrl(property.tipo);
  const area =
    property.total_area_privativa ||
    property.total_area ||
    property.total_area_terreno;

  const url = `${tipo}-${property.banco}-em-${cidade}-${bairro}-${endereco}-${property.uf}`;

  if (area) {
    return `${url}-${area}m2`.toLowerCase();
  }

  return url.toLowerCase();
};

const formatPropertyRegistration = (value?: string) => {
  if (!value) return "";

  return value.length < 13 ? `00000${value}` : value;
};

export const formatPropertyRegistrationUrl = ({
  banco,
  numero_do_leilao,
  uf,
}: EstateWithBank & { uf: string }) => {
  if (banco === banks.caixa.value) {
    return `https://venda-imoveis.caixa.gov.br/editais/matricula/${uf}/${formatPropertyRegistration(
      numero_do_leilao
    )}.pdf`;
  }

  return "";
};

const formatImage = (
  property: Pick<LeilaoSQLCustom, "banco" | "imagens" | "numero_do_leilao">
) => {
  if (property.banco === banks.caixa.value) {
    return Array.from({ length: 4 }).map((_, index) => {
      const newIndex = index + 1;

      return `https://venda-imoveis.caixa.gov.br/fotos/F${formatPropertyRegistration(
        property.numero_do_leilao!
      )}2${newIndex}.jpg`;
    });
  }

  return property.imagens?.split(",") || [];
};

const formatDescription = (description: string | null, banco: string) => {
  if (!description) return "";

  if (banco === banks.caixa.value) {
    return description.replace(/,/g, ", ");
  }

  return description;
};

export const formatProperty = (property: LeilaoSQLCustom) => {
  const price = calculatePrice(property);
  const valorArrematacao = Number(price);
  const valorAvaliacao = Number(property.valor_de_avaliacao);

  const {
    valor_de_desconto,
    valor_gasto_inicial,
    valor_gasto_mensal,
    valor_lucro_liquido_sozinho,
    valor_lucro_liquido_com_corretor,
    roi_com_corretor,
    roi_solo,
    projecao_de_lucro,
  } = calculateGeneralValues({
    uf: property.uf as House["uf"],
    valor_arrematacao: valorArrematacao,
    valor_avaliacao: valorAvaliacao,
    tipo: property.tipo ?? undefined,
    banco: property.banco,
    modalidade_de_venda: (property.modalidade_de_venda ??
      undefined) as Modality,
  });

  const FOUR_DAYS_IN_SECONDS = 4 * 24 * 60 * 60;

  const daysDifference = property.criado_em
    ? Math.floor(todayUnixUTC / 1000) -
      Math.floor(new Date(property.criado_em).getTime() / 1000)
    : null;
  const isNew =
    daysDifference !== null && daysDifference <= FOUR_DAYS_IN_SECONDS;

  const preco1 = property.preco1 || 0;
  const preco2 = property.preco2 || 0;

  return {
    ...property,
    preco_atual: price,
    preco1,
    preco2,
    tipo: property.tipo,
    descricao: formatDescription(property.descricao, property.banco),
    valor_de_desconto,
    desconto:
      property.desconto ||
      calculateDiscount({
        auctionPrice: calculatePrice({ preco1, preco2 }) || 0,
        valuationPrice: property.valor_de_avaliacao || 0,
      }),
    valor_gasto_inicial,
    valor_gasto_mensal,
    valor_lucro_liquido_sozinho,
    valor_lucro_liquido_com_corretor,
    roi_com_corretor,
    roi_solo,
    projecao_de_lucro,
    acoes_judiciais: property.acoes_judiciais
      ? JSON.parse(property.acoes_judiciais)
      : [],
    novo: isNew,
    imagens: formatImage(property),
  };
};

export const formatImageLdJson = (
  bank: LeilaoSQLCustom["banco"],
  images: string[]
) => {
  if (bank === banks.caixa.value) {
    return [images[0]];
  }

  return images;
};

export function generateKeywords({
  baseArea,
  bankInfo,
  citiesSearchParams,
  type,
  modalities,
  payments,
  isBankPage,
  endereco,
}: {
  baseArea: string;
  bankInfo?: string;
  citiesSearchParams?: string;
  type?: string;
  modalities?: string;
  payments?: string;
  isBankPage?: boolean;
  endereco?: string;
}) {
  return [
    // Base location keywords
    `leilões de imóveis${bankInfo ? ` ${bankInfo}` : ""} em ${baseArea}`,
    `imóveis em leilão${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,
    `leilão de imóveis${bankInfo ? ` ${bankInfo}` : ""} em ${baseArea}`,
    `imóveis de leilão${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,

    // Address specific keywords
    ...(endereco
      ? [
          `leilão de imóveis${bankInfo ? ` ${bankInfo}` : ""} ${endereco}`,
          `imóveis em leilão${bankInfo ? ` ${bankInfo}` : ""} ${endereco}`,
          `leilão${bankInfo ? ` ${bankInfo}` : ""} ${endereco}`,
          ...(type
            ? [
                `${typeCriteria[type as keyof typeof typeCriteria]} em leilão${bankInfo ? ` ${bankInfo}` : ""} ${endereco}`,
              ]
            : []),
        ]
      : []),

    // City specific keywords
    ...(citiesSearchParams
      ? citiesSearchParams
          .split(",")
          .map((city) => [
            `leilões de imóveis${bankInfo ? ` ${bankInfo}` : ""} em ${city}`,
            `imóveis em leilão${bankInfo ? ` ${bankInfo}` : ""} ${city}`,
            `leilão de imóveis${bankInfo ? ` ${bankInfo}` : ""} ${city}`,
            `imóveis de leilão${bankInfo ? ` ${bankInfo}` : ""} ${city}`,
            ...(endereco
              ? [`leilão${bankInfo ? ` ${bankInfo}` : ""} ${endereco} ${city}`]
              : []),
          ])
          .flat()
      : []),

    // Bank specific keywords (only for non-bank pages)
    ...(!isBankPage
      ? bankInfo
        ? [
            `leilão ${bankInfo} ${baseArea}`,
            `imóveis ${bankInfo} ${baseArea}`,
            ...(citiesSearchParams
              ? citiesSearchParams
                  .split(",")
                  .map((city) => `leilão ${bankInfo} ${city}`)
              : []),
          ]
        : [
            `leilão caixa ${baseArea}`,
            ...(citiesSearchParams
              ? citiesSearchParams
                  .split(",")
                  .map((city) => `leilão caixa ${city}`)
              : []),
          ]
      : []),

    // Type specific keywords
    ...(type
      ? [
          `${typeCriteria[type as keyof typeof typeCriteria]} em leilão${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,
          `leilão de ${typeCriteria[type as keyof typeof typeCriteria]?.toLowerCase()}${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,
          ...(citiesSearchParams
            ? citiesSearchParams
                .split(",")
                .map(
                  (city) =>
                    `${typeCriteria[type as keyof typeof typeCriteria]} em leilão${bankInfo ? ` ${bankInfo}` : ""} ${city}`
                )
            : []),
        ]
      : []),

    // Modality specific keywords
    ...(modalities
      ? modalities
          .split(",")
          .map((m) => [
            `${modalityCriteria[m as keyof typeof modalityCriteria]}${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,
            ...(citiesSearchParams
              ? citiesSearchParams
                  .split(",")
                  .map(
                    (city) =>
                      `${modalityCriteria[m as keyof typeof modalityCriteria]}${bankInfo ? ` ${bankInfo}` : ""} ${city}`
                  )
              : []),
          ])
          .flat()
      : []),

    // Payment specific keywords
    ...(payments
      ? [
          `imóveis ${payments}${bankInfo ? ` ${bankInfo}` : ""} ${baseArea}`,
          ...(citiesSearchParams
            ? citiesSearchParams
                .split(",")
                .map(
                  (city) =>
                    `imóveis ${payments}${bankInfo ? ` ${bankInfo}` : ""} ${city}`
                )
            : []),
        ]
      : []),

    // Generic keywords
    "venda direta imóveis",
    "leilão judicial imóveis",
    "leilão extrajudicial imóveis",
  ].filter(Boolean);
}
